import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function SubmissionTab() {
  const [queue, setQueue] = useState([]);
  const [bulk, setBulk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [exportPreview, setExportPreview] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [dispatching, setDispatching] = useState(false);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qR, bR] = await Promise.all([
        api.get('/rcm/submission/queue'),
        api.get('/rcm/submission/readiness-bulk?limit=30'),
      ]);
      setQueue(qR.data.queue || []);
      setBulk(bR.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (claimId) => {
    setSelected(claimId);
    setExportPreview(null);
    setAttempts([]);
    try {
      const [rR, sR, drR] = await Promise.all([
        api.get(`/rcm/submission/readiness/${claimId}`),
        api.get(`/rcm/submission/claim/${claimId}/submissions`),
        api.get(`/rcm/predictive/denial-risk/${claimId}`).catch(() => ({ data: null })),
      ]);
      const subs = sR.data.submissions || [];
      setDetail({ readiness: rR.data, submissions: subs, denialRisk: drR.data });
      // Auto-load export preview and attempts for the latest submission
      if (subs.length > 0) {
        const latestSub = subs[subs.length - 1];
        loadExportPreview(latestSub.id);
        loadAttempts(latestSub.id);
      }
    } catch { setDetail(null); }
  };

  const loadExportPreview = async (subId) => {
    try {
      const r = await api.post('/rcm/external-submissions/export-preview', { claim_submission_id: subId });
      setExportPreview(r.data);
    } catch { setExportPreview(null); }
  };

  const loadAttempts = async (subId) => {
    try {
      const r = await api.get(`/rcm/external-submissions/${subId}/attempts`);
      setAttempts(r.data.attempts || []);
    } catch { setAttempts([]); }
  };

  const markReady = async (claimId) => {
    try { await api.post(`/rcm/submission/${claimId}/mark-ready`); load(); openDetail(claimId); } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };
  const submitClaim = async (claimId) => {
    try { await api.post(`/rcm/submission/${claimId}/submit`); load(); openDetail(claimId); } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const dispatchExternal = async (subId) => {
    setDispatching(true);
    try {
      await api.post('/rcm/external-submissions/dispatch', { claim_submission_id: subId, environment: 'sandbox' });
      loadAttempts(subId);
      loadExportPreview(subId);
    } catch(e) { alert(e.response?.data?.detail || 'Dispatch failed'); }
    finally { setDispatching(false); }
  };

  const retryAttempt = async (attemptId, subId) => {
    setRetrying(attemptId);
    try {
      await api.post(`/rcm/external-submissions/${attemptId}/retry`);
      loadAttempts(subId);
    } catch(e) { alert(e.response?.data?.detail || 'Retry failed'); }
    finally { setRetrying(null); }
  };

  if (loading && !bulk) return <div className="p-4 text-sm text-muted-foreground">Loading submission queue...</div>;

  const latestSub = detail?.submissions?.length > 0 ? detail.submissions[detail.submissions.length - 1] : null;

  return (
    <div className="space-y-4" data-testid="submission-tab">
      {/* Summary strip */}
      <div className="flex gap-3">
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{bulk?.ready_count || 0}</p><p className="text-[10px] text-muted-foreground uppercase">Ready</p>
        </CardContent></Card>
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-red-600">{bulk?.blocked_count || 0}</p><p className="text-[10px] text-muted-foreground uppercase">Blocked</p>
        </CardContent></Card>
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold">{queue.length}</p><p className="text-[10px] text-muted-foreground uppercase">In Queue</p>
        </CardContent></Card>
      </div>

      {/* Readiness table */}
      <Card>
        <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
          <CardTitle className="text-xs">Claim Readiness</CardTitle>
          <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={load}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <table className="w-full text-xs" data-testid="readiness-table">
            <thead><tr className="border-b text-left">
              <th className="py-1.5 font-medium text-muted-foreground">Claim</th>
              <th className="py-1.5 font-medium text-muted-foreground">Payer</th>
              <th className="py-1.5 font-medium text-muted-foreground">Status</th>
              <th className="py-1.5 font-medium text-muted-foreground text-right">Score</th>
              <th className="py-1.5 font-medium text-muted-foreground">Issues</th>
              <th className="py-1.5"></th>
            </tr></thead>
            <tbody>{bulk?.results?.map((r) => (
              <tr key={r.claim_id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(r.claim_id)}>
                <td className="py-1.5 font-mono text-[10px]">{r.claim_id?.slice(0,8)}...</td>
                <td className="py-1.5">{r.payer_name || r.payer_id || '—'}</td>
                <td className="py-1.5"><StatusChip status={r.claim_status} size="xs" /></td>
                <td className="py-1.5 text-right">
                  <span className={`font-medium ${r.ready ? 'text-emerald-600' : 'text-red-600'}`}>{r.readiness_score}%</span>
                </td>
                <td className="py-1.5">
                  {r.blocking_issues?.length > 0 ? (
                    <span className="text-red-600">{r.blocking_issues.length} blocker{r.blocking_issues.length > 1 ? 's' : ''}</span>
                  ) : r.warnings?.length > 0 ? (
                    <span className="text-amber-600">{r.warnings.length} warning{r.warnings.length > 1 ? 's' : ''}</span>
                  ) : <span className="text-emerald-600"><CheckCircle2 className="h-3 w-3 inline" /> Ready</span>}
                </td>
                <td className="py-1.5"><ChevronRight className="h-3 w-3 text-muted-foreground" /></td>
              </tr>
            ))}</tbody>
          </table>
        </CardContent>
      </Card>

      {/* Detail Panel */}
      <SlidePanel open={!!selected} onClose={() => { setSelected(null); setDetail(null); setExportPreview(null); setAttempts([]); }} title="Submission Detail">
        {detail && (<>
          {/* Readiness */}
          <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Readiness ({detail.readiness.readiness_score}%)</CardTitle></CardHeader>
            <CardContent className="px-3 pb-3 space-y-1">
              {detail.readiness.blocking_issues?.map((b,i) => (
                <div key={i} className="flex gap-2 text-xs"><XCircle className="h-3 w-3 text-red-500 shrink-0 mt-0.5" /><span>{b.message}</span></div>
              ))}
              {detail.readiness.warnings?.map((w,i) => (
                <div key={i} className="flex gap-2 text-xs"><AlertTriangle className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" /><span>{w.message}</span></div>
              ))}
              {detail.readiness.blocking_issues?.length === 0 && detail.readiness.warnings?.length === 0 && (
                <div className="flex gap-2 text-xs text-emerald-600"><CheckCircle2 className="h-3 w-3 shrink-0 mt-0.5" />All checks passed</div>
              )}
          </CardContent></Card>

          {/* Actions */}
          <div className="flex gap-2">
            {detail.readiness.ready && (
              <>
                <Button size="sm" className="h-7 text-xs" variant="outline" onClick={() => markReady(selected)} data-testid="action-mark-ready">
                  <CheckCircle2 className="h-3 w-3 mr-1" />Mark Ready
                </Button>
                <Button size="sm" className="h-7 text-xs" onClick={() => submitClaim(selected)} data-testid="action-submit">
                  <Send className="h-3 w-3 mr-1" />Submit
                </Button>
              </>
            )}
          </div>

          {/* Predictive: Denial Risk */}
          {detail.denialRisk && detail.denialRisk.risk_level && (
            <Card className="border-violet-200 bg-violet-50/20" data-testid="denial-risk-card">
              <CardContent className="p-3 space-y-1.5 text-xs">
                <div className="flex items-center justify-between">
                  <PredictiveLabel />
                  <div className="flex items-center gap-2">
                    <RiskBadge level={detail.denialRisk.risk_level} label={`${detail.denialRisk.risk_level} denial`} />
                    <FeedbackButtons predictionId={detail.denialRisk.prediction_id} />
                  </div>
                </div>
                <p className="text-muted-foreground">Confidence: <ConfidenceText level={detail.denialRisk.confidence > 70 ? 'high' : detail.denialRisk.confidence > 40 ? 'medium' : 'low'} score={detail.denialRisk.confidence} /></p>
                {detail.denialRisk.predicted_reason && <p>Most likely: <span className="font-medium">{detail.denialRisk.predicted_reason}</span></p>}
                {detail.denialRisk.supporting_factors?.slice(0, 3).map((f, i) => (
                  <div key={i} className="flex gap-1.5 text-muted-foreground"><span className="text-violet-500">·</span>{f.detail}</div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* ═══ PHASE 6: Export Preview Panel ═══ */}
          {exportPreview && (
            <Card className="border-cyan-200 bg-cyan-50/10" data-testid="export-preview-panel">
              <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
                <CardTitle className="text-xs flex items-center gap-1.5">
                  <Package className="h-3 w-3 text-cyan-600" />Export Preview
                </CardTitle>
                <EnvironmentBadge env={exportPreview.environment} />
              </CardHeader>
              <CardContent className="px-3 pb-3 space-y-2 text-xs">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <div><span className="text-muted-foreground">Payer:</span> <span className="font-medium">{exportPreview.payer?.payer_name || '—'}</span></div>
                  <div><span className="text-muted-foreground">Format:</span> <span className="font-medium">{exportPreview.payload_format}</span></div>
                  <div><span className="text-muted-foreground">Total:</span> <span className="font-medium">{fmt(exportPreview.total_charge_amount)}</span></div>
                  <div><span className="text-muted-foreground">Vendor:</span> <span className="font-medium">{exportPreview.transport_vendor}</span></div>
                </div>
                {exportPreview.diagnosis_codes?.length > 0 && (
                  <div><span className="text-muted-foreground">Dx:</span> {exportPreview.diagnosis_codes.map((c,i) => <Badge key={i} variant="outline" className="text-[9px] mr-1 py-0">{c}</Badge>)}</div>
                )}
                {exportPreview.charge_lines?.length > 0 && (
                  <div className="space-y-0.5">
                    <span className="text-muted-foreground">CPT Lines:</span>
                    {exportPreview.charge_lines.map((l,i) => (
                      <div key={i} className="flex justify-between pl-2 border-l-2 border-cyan-200">
                        <span><span className="font-mono font-medium">{l.cpt_code}</span> {l.description && <span className="text-muted-foreground">— {l.description}</span>}</span>
                        <span className="font-medium">{l.units}x {fmt(l.charge_amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Validation state */}
                {exportPreview.validation && (
                  <div className={`rounded p-2 ${exportPreview.validation.export_ready ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`} data-testid="export-validation">
                    {exportPreview.validation.export_ready ? (
                      <div className="flex items-center gap-1.5 text-emerald-700"><CheckCircle2 className="h-3 w-3" />Ready for external dispatch</div>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-red-700"><XCircle className="h-3 w-3" />Not ready for dispatch</div>
                        {exportPreview.validation.blocking_issues?.map((b,i) => (
                          <div key={i} className="text-red-600 pl-4">• {b.message}</div>
                        ))}
                      </div>
                    )}
                    {exportPreview.validation.warnings?.map((w,i) => (
                      <div key={i} className="text-amber-600 pl-4 flex items-center gap-1"><AlertTriangle className="h-2.5 w-2.5" />{w.message}</div>
                    ))}
                  </div>
                )}
                {/* Dispatch Button */}
                {exportPreview.validation?.export_ready && latestSub && (
                  <Button
                    size="sm" className="h-7 text-xs w-full"
                    onClick={() => dispatchExternal(latestSub.id)}
                    disabled={dispatching}
                    data-testid="dispatch-btn"
                  >
                    {dispatching ? <><RotateCw className="h-3 w-3 mr-1 animate-spin" />Dispatching...</> : <><Radio className="h-3 w-3 mr-1" />Dispatch to Sandbox</>}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* ═══ PHASE 6: Attempt History ═══ */}
          {attempts.length > 0 && (
            <Card data-testid="attempt-history-panel">
              <CardHeader className="pb-1 pt-3 px-3">
                <CardTitle className="text-xs flex items-center gap-1.5">
                  <Radio className="h-3 w-3" />Transport Attempts ({attempts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3 space-y-2">
                {attempts.map((a) => (
                  <div key={a.id} className="border rounded p-2 text-xs space-y-1" data-testid={`attempt-${a.attempt_number}`}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Attempt #{a.attempt_number}</span>
                        <TransportStatusChip status={a.transport_status} />
                      </div>
                      <EnvironmentBadge env={a.environment} />
                    </div>
                    {a.external_reference_id && (
                      <div className="text-muted-foreground flex items-center gap-1"><Hash className="h-2.5 w-2.5" />Ref: <span className="font-mono">{a.external_reference_id}</span></div>
                    )}
                    {a.external_tracking_id && (
                      <div className="text-muted-foreground flex items-center gap-1"><Hash className="h-2.5 w-2.5" />Track: <span className="font-mono">{a.external_tracking_id}</span></div>
                    )}
                    {a.error_summary && (
                      <div className="text-red-600 bg-red-50 rounded p-1.5">{a.error_summary}</div>
                    )}
                    <div className="flex justify-between text-muted-foreground">
                      <span>{a.transport_started_at ? new Date(a.transport_started_at).toLocaleString() : '—'}</span>
                      {a.retry_eligible && latestSub && (
                        <Button
                          variant="outline" size="sm" className="h-5 text-[10px] px-2"
                          onClick={() => retryAttempt(a.id, latestSub.id)}
                          disabled={retrying === a.id}
                          data-testid={`retry-btn-${a.attempt_number}`}
                        >
                          {retrying === a.id ? <RotateCw className="h-2.5 w-2.5 animate-spin" /> : <><RotateCw className="h-2.5 w-2.5 mr-0.5" />Retry</>}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Submission history (original) */}
          {detail.submissions?.length > 0 && (
            <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Submission Attempts ({detail.submissions.length})</CardTitle></CardHeader>
              <CardContent className="px-3 pb-3 space-y-2">
                {detail.submissions.map((s,i) => (
                  <div key={i} className="border-b pb-2 last:border-0 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Attempt #{s.submission_attempt_number}</span>
                      <StatusChip status={s.submission_status} size="xs" />
                    </div>
                    {s.external_reference_id && <p className="text-muted-foreground mt-0.5">Ref: {s.external_reference_id}</p>}
                    {s.readiness_snapshot && <p className="text-muted-foreground">Readiness at submit: {s.readiness_snapshot.readiness_score}%</p>}
                  </div>
                ))}
            </CardContent></Card>
          )}
        </>)}
      </SlidePanel>
    </div>
  );
}

// ═══════════════════════════════════════
// REJECTIONS TAB (Phase 6 Slice 2 Enhanced)
// ═══════════════════════════════════════

export { SubmissionTab };
